import React, { FormEvent, useState } from "react"
import styled, { css } from "styled-components"
import Tick from "../../images/icons/tick.inline.svg"
import { RawLink } from "../../ui/components/link/link.component"
import {
  NewsletterSuccess,
  NewsletterError,
} from "./newsletter-norification.component"
import { TextInput } from "./text-input.component"
import useNewsletter from "../../hooks/use-newsletter.hook"
import { Path } from "../../navigation/constants/routes"
import { BasicButton } from "../../ui/components/button/button.component"
import useTranslationHook from "../../hooks/use-translation.hook"
import { AppFunctionComponent } from "../../types"
import fbTrack from "../../helpers/fbTrack"

export interface NewsletterProps {
  split?: boolean
  channel?: string
  buttonText?: string
  disclaimerText?: string
  mailchimpList?: string
  className?: string
  tags?: string[]
  successMessage?: string
  status?: "subscribed" | "pending"
}

const NameInput = styled(TextInput)`
  grid-area: Name;
`
const EmailInput = styled(TextInput)`
  grid-area: Email;
`

const subscribedButtonStyles = css`
  background-color: ${({ theme }) => theme.color.buttonSuccess};
  pointer-events: none;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.color.buttonSuccess};
  }
`

const initialButtonStyles = css`
  background-color: ${({ theme }) => theme.color.button};
  pointer-events: unset;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.color.buttonActive};
  }
`

export const SubscribeButton = styled(BasicButton)<{
  subscribe: boolean
  pending?: boolean
}>`
  grid-area: Button;
  pointer-events: ${({ disabled }) => (disabled ? "none" : "unset")};

  margin: 0 2px;
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    margin: 5px;
  }

  ${({ subscribe }) =>
    subscribe ? subscribedButtonStyles : initialButtonStyles}
`

const NewsletterContainer = styled.form<{ split: boolean }>`
  flex: 1;
  margin: 10px -3px -3px -10px;
  display: grid;
  padding: 0 5px;
  max-width: 100%;
  grid-template-rows: auto 1fr;
  grid-template-columns: 1fr;
  grid-template-areas: "Error Error Error" "Name Email Button" "Annotation Annotation Annotation";
  position: relative;

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr 2fr 150px;
    grid-template-areas: "Error Error Error" "Name Email Button" "Annotation Annotation Annotation";
    p {
      text-align: right;
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-areas: "Error Error Error Error" "Name Name Email Email" "Button Button Button Button" "Annotation Annotation Annotation Annotation";
  }
  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    margin-top: 25px;
  }

  p {
    width: 100%;
    margin-top: 15px;
    text-align: center;
    font-size: ${11 / 16}rem;
    color: ${({ theme }) => theme.color.newsletterAgreement};
    grid-area: Annotation;
  }

  ${SubscribeButton} {
    max-width: 100%;
    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      ${({ split }) =>
        split &&
        css`
          flex-basis: auto;
        `}
    }
    @media (max-width: ${({ theme }) =>
        theme.breakpoint.mobileHorizontal - 1}px) {
      flex-basis: 100%;
    }
  }
  ${({ split }) =>
    split &&
    css`
      margin-top: 25px;
    `}
`

export const NewsletterTick = styled(Tick)`
  height: 11px;
  width: 12px;
  margin-right: 5px;
`

const Newsletter: AppFunctionComponent<NewsletterProps> = ({
  className,
  split = false,
  channel = "Mudita website",
  buttonText = "Subscribe",
  disclaimerText = "By providing your name and e-mail you agree to receive marketing content and commercial offers from Mudita Sp. z o.o. with its registered office in Warsaw.",
  mailchimpList,
  tags,
  status,
  successMessage,
}) => {
  const { error, loading, subscribeToNewsletter } = useNewsletter()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [subscribed, setSubscriptionSuccess] = useState(false)
  const { language, translate, dynamicTranslate } = useTranslationHook()

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    await subscribeToNewsletter({
      name,
      email,
      channel,
      mailchimpList,
      language,
      tags,
      status,
    })
    setSubscriptionSuccess(true)

    if (!error && !loading) {
      fbTrack("track", "CompleteRegistration")
    }
  }
  const translatedButtonText = dynamicTranslate(buttonText)
  const translatedDisclaimerText = dynamicTranslate(disclaimerText)
  const errorId = "newsletter-error"
  const privacyMessageId = "privacy-message"

  return (
    <NewsletterContainer
      className={className}
      split={split}
      name="newsletter"
      method="post"
      onSubmit={submit}
    >
      {error && (
        <NewsletterError
          id={errorId}
          error={
            error?.response?.data?.message ??
            "Something went wrong. Please try again later."
          }
          email={email}
        />
      )}
      {!loading && subscribed && !error && (
        <NewsletterSuccess message={successMessage} />
      )}
      <NameInput
        className={""}
        type={"text"}
        name={"name"}
        placeholder={""}
        label={translate("newsletter-your-name")}
        value={name}
        onValueChange={setName}
        minLength={3}
        aria-describedby={`${errorId} ${privacyMessageId}`}
      />
      <EmailInput
        className={""}
        type={"text"}
        name={"email"}
        placeholder={""}
        label={translate("newsletter-your-email")}
        value={email}
        onValueChange={setEmail}
        minLength={3}
        aria-describedby={`${errorId} ${privacyMessageId}`}
      />
      <SubscribeButton
        subscribe={!error && subscribed}
        pending={loading}
        type="submit"
      >
        {subscribed && !error ? (
          <>
            <NewsletterTick alt="Success tick" /> Sent
          </>
        ) : (
          translatedButtonText
        )}
      </SubscribeButton>
      <p id={privacyMessageId}>
        {translatedDisclaimerText}
        {translate("newsletter-disclaimer")}
        <RawLink to={Path.PrivacyPolicy}>{translate("Privacy Policy")}</RawLink>
        {translate("newsletter-disclaimer-end")}
        <RawLink to={Path.NewsletterTerms}>
          {translate("newsletter-terms")}
          {translate("newsletter-label")}
        </RawLink>
      </p>
    </NewsletterContainer>
  )
}

export default styled(Newsletter)``
